import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, ToggleLeft, ToggleRight, AlertTriangle, Info } from 'lucide-react';
import Card from '../../components/Card';
import { getRosterMode, setRosterMode } from '../../utils/api';
import toast from 'react-hot-toast';

export default function BasketballSettings() {
    const queryClient = useQueryClient();

    // Fetch roster mode
    const { data: rosterModeData, isLoading } = useQuery({
        queryKey: ['roster-mode'],
        queryFn: getRosterMode
    });

    // Toggle mutation
    const toggleMutation = useMutation({
        mutationFn: (useCustomRoster) => setRosterMode(useCustomRoster),
        onSuccess: (result) => {
            const mode = result.data?.useCustomRoster ? 'Custom Roster' : 'Genius Sports';
            toast.success(`Roster mode switched to: ${mode}`);
            queryClient.invalidateQueries(['roster-mode']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to update roster mode');
        }
    });

    const useCustomRoster = rosterModeData?.data?.useCustomRoster || false;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold mb-2">Basketball Settings</h1>
                <p className="text-muted-light dark:text-muted-dark">
                    Configure basketball data settings
                </p>
            </div>

            {/* Roster Mode Toggle */}
            <Card>
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                        <Settings size={24} className="text-primary" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-semibold mb-2">Roster Data Mode</h2>
                        <p className="text-muted-light dark:text-muted-dark mb-4">
                            Control where team roster data comes from in the mobile app.
                        </p>

                        {/* Current Status */}
                        <div className={`p-4 rounded-lg border mb-4 ${useCustomRoster
                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                            }`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-medium text-lg">
                                        {useCustomRoster ? 'Custom Roster Mode' : 'Genius Sports Mode'}
                                    </div>
                                    <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                        {useCustomRoster
                                            ? 'Teams with custom rosters will show custom data. Others fall back to Genius.'
                                            : 'All teams show data from Genius Sports API (current behavior).'
                                        }
                                    </p>
                                </div>
                                <button
                                    onClick={() => toggleMutation.mutate(!useCustomRoster)}
                                    disabled={isLoading || toggleMutation.isPending}
                                    className="flex-shrink-0 p-2 hover:bg-white/50 dark:hover:bg-black/20 rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {useCustomRoster ? (
                                        <ToggleRight size={48} className="text-green-600" />
                                    ) : (
                                        <ToggleLeft size={48} className="text-gray-400" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Explanation */}
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <p className="font-medium text-blue-800 dark:text-blue-200">
                                        How it works:
                                    </p>
                                    <ul className="mt-2 space-y-1 text-blue-700 dark:text-blue-300">
                                        <li>• <strong>OFF:</strong> All roster data comes from Genius Sports API</li>
                                        <li>• <strong>ON:</strong> Teams with custom rosters show custom data</li>
                                        <li>• Teams without custom rosters always fall back to Genius data</li>
                                        <li>• Player statistics always come from Genius Sports (box scores)</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                                        Important:
                                    </p>
                                    <ul className="mt-2 space-y-1 text-yellow-700 dark:text-yellow-300">
                                        <li>• Prepare all custom rosters before turning this ON</li>
                                        <li>• Changes take effect immediately for all users</li>
                                        <li>• Custom people must be linked to Genius Person IDs for stats to work</li>
                                        <li>• Test on a team before enabling globally</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Instructions */}
            <Card>
                <h3 className="font-semibold mb-4">Setup Guide</h3>
                <ol className="space-y-3 text-sm">
                    <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">1</span>
                        <div>
                            <strong>Create Custom People</strong>
                            <p className="text-muted-light dark:text-muted-dark">
                                Go to "People" and create custom players/staff linked to Genius Sports IDs.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">2</span>
                        <div>
                            <strong>Build Team Rosters</strong>
                            <p className="text-muted-light dark:text-muted-dark">
                                Go to "Teams", select a team, and add custom people to their roster.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">3</span>
                        <div>
                            <strong>Test with Mode OFF</strong>
                            <p className="text-muted-light dark:text-muted-dark">
                                Keep the toggle OFF while setting up. Verify all data is correct.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-medium">4</span>
                        <div>
                            <strong>Enable Custom Mode</strong>
                            <p className="text-muted-light dark:text-muted-dark">
                                Once all rosters are ready, turn ON the toggle to switch to custom data.
                            </p>
                        </div>
                    </li>
                </ol>
            </Card>
        </div>
    );
}

